import {
    AgGrid,
    AgGridAutoRefresh,
    AgGridColumnMenuTrigger,
    AgGridExportMenu,
    AgGridPagination,
    AgGridProvider,
    AgGridToggleShowFilters,
    NyUppercaseInput
} from 'nyse-web-tools-common/lib/react';
import connect from 'nyse-web-tools-common/lib/utils/redux.connect';
import {useSnackbar} from 'notistack';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';

import {AddAppointmentsModal} from '../components/modals/mmAppointments/addAppointments.modal';
import {DeleteConfirmationModal} from '../components/modals/mmAppointments/deleteConfirmation.modal';
import {EntitlementSelect} from '../components/selects/entitlement.select';
import {FirmSelect} from '../components/selects/firm.select';
import {MmIdSelect} from '../components/selects/mmid.select';
import {MMTypesSelect} from '../components/selects/mmType.select';
import {MmAppointmentHoldWindowInfoTooltip} from '../components/tooltips/MmAppointmentHoldWindowInfoTooltip';
import {MmAppointmentPermissions} from '../constants/permissions';
import {useEntitlement} from '../hooks/useEntitlement';
import useStore from '../hooks/useStore.hook';
import useUserFirms from '../hooks/useUserFirms';
import {setColumns} from '../stores/columnDefs/columnDefs.actions';
import {
    createMmAppointments,
    deleteMmAppointments,
    getMmAppointments
} from '../stores/mmAppointments/mmAppointments.actions';
import {
    buildMmTypeEligibilityByLabel,
    isMmAppointmentRowSelectable
} from '../stores/mmAppointments/mmAppointments.helpers';
import {getMMTypes} from '../stores/mmTypes/mmTypes.actions';
import {hasPermissions} from '../utils/utilities';

const MAX_SELECTION = 25;

const MmAppointmentsPageComponent = (props) => {
    const {enqueueSnackbar} = useSnackbar();

    const [filterValues, setFilterValues] = useState({});
    const [selectedRows, setSelectedRows] = useState([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const userFirms = useUserFirms();
    // ctx.entitlement.name is only populated for super users, who pick an entitlement
    // via EntitlementSelect. Every other user type carries a single fixed entitlement on
    // their user record, so fall back to it (TOP-5767 -- without the fallback this stayed
    // undefined and the readiness gate below never opened, rendering nothing at all).
    //
    // Do NOT "fix" this by writing user.entitlement into the store instead: sentinel's
    // /firmAttributes is super-user-only and throws "Only applicable to super user", and
    // useUserFirms deliberately skips that call while the store entitlement is falsy.
    // Populating the store makes the call fire and 500 in a retry loop.
    const entitlement = useEntitlement() || props.user.entitlement;

    const dispatch = useDispatch();
    const mmTypes = useStore('mmTypes');

    // Load the MM-type registry up front. It feeds the MM Type filter and tags
    // each type with `eligible`; we use that flag to lock non-self-serviceable
    // rows (CRS-managed SLMM/DMM) out of selection in the grid below.
    useEffect(() => {
        dispatch(getMMTypes());
    }, [dispatch]);

    const mmTypeOptions = useMemo(
        () => (mmTypes && mmTypes.options) || [],
        [mmTypes]
    );

    const mmTypeEligibility = useMemo(
        () => buildMmTypeEligibilityByLabel(mmTypeOptions),
        [mmTypeOptions]
    );

    // ag-grid evaluates this per row: ineligible MM types render a disabled
    // checkbox and are skipped by row-click + header select-all, so the user
    // can't queue a delete the backend would only reject.
    const isRowSelectable = useCallback(
        (node) => isMmAppointmentRowSelectable(node && node.data, mmTypeEligibility),
        [mmTypeEligibility]
    );

    // Source the default firm + MMID from useUserFirms, i.e. the same data that
    // feeds the Firm and MMID dropdowns, so the auto-submit can never disagree with
    // the dropdowns. Which list that is depends on the user type, and both are
    // correct: a super user impersonating an entitlement gets sentinel
    // firmAttributes, while a non-super-user gets ctx.firms (= props.firms), the
    // login payload, because that is the only firm list they are issued. Do not
    // read props.firms directly here -- under super-user impersonation it is the
    // wrong list, since it does not track the selected entitlement.
    const activeFirms = useMemo(
        () => (userFirms.data ?? []).filter((firm) => firm.active),
        [userFirms.data]
    );

    const resolvedCrd = useMemo(() => {
        const routeCrd = props.route.params.crd;

        return routeCrd && activeFirms.some((firm) => firm.crd === routeCrd)
            ? routeCrd
            : activeFirms[0]?.crd;
    }, [props.route.params.crd, activeFirms]);

    const firstMmid = useMemo(() => {
        const firm = activeFirms.find((f) => f.crd === resolvedCrd);
        const mmId = (firm?.participants ?? [])
            .flatMap((participant) => participant.mmIds ?? [])
            .find((entry) => entry && entry.id);

        return mmId ? mmId.id : '';
    }, [activeFirms, resolvedCrd]);

    // Hold the on-load search until the dropdowns are populated: the entitlement
    // must be resolved and firmAttributes (which feeds the Firm + MMID selects)
    // must have finished fetching, so the auto-submit carries a real MMID rather
    // than firing a doomed empty-MMID request.
    //
    // NOTE: do NOT also gate this on mm-types loading. MMTypesSelect (rendered
    // inside the grid) re-dispatches getMMTypes on mount, and MM_TYPES_GET
    // transiently empties `options`; gating here would flip isReady false, unmount
    // the grid, remount the select, and loop forever. Eligibility is instead
    // fetched at page level (above) in parallel with firmAttributes, so it is
    // populated by the time the grid mounts; until it is, isRowSelectable just
    // fails open (server still backstops the delete).
    //
    // Gate on isFetched, NOT on !isFetching (TOP-5768). FirmSelect and MmIdSelect
    // call useUserFirms() themselves, so they observe the very query this gate
    // watches. A gate that re-arms on every fetch closes a feedback loop:
    // isFetching -> `return null` unmounts them -> the query settles -> they
    // remount -> the mount fetch fires -> isFetching. That mount fetch is
    // shouldLoadOnMount, not shouldRefetchOnMount: after a cold failure
    // dataUpdatedAt is still 0 and retryOnMount defaults true, so neither
    // staleTime nor refetchOnMount damps it. That is react-query 3.10.0
    // behaviour and worth re-checking on upgrade. The loop is unbounded while
    // firmAttributes returns non-2xx: reverting this one line makes the page spec
    // OOM-kill node at ~1700 query executions, and a browser measured ~80
    // firmAttributes/sec plus ~40 /api/mm-types (MMTypesSelect remounts with the
    // rest of the tree). It is the same unmount/remount shape the mm-types note
    // above warns about, one query over.
    //
    // isFetched is `dataUpdateCount > 0 || errorUpdateCount > 0`; both counters
    // only ever increment, so the gate latches on the first settle (success OR
    // failure). That preserves the hold this gate exists for -- first paint still
    // waits for the dropdowns -- without letting a later fetch tear the tree
    // down. The latch is per query key: a real entitlement or MIC change builds a
    // new cache entry and closes the gate once more, which is bounded and
    // intended.
    const isReady = Boolean(entitlement) && userFirms.isFetched;

    const initialPagination = useMemo(() => ({
        limit: 25,
        offset: 0
    }), []);

    const initialFilters = useMemo(() => ({
        userCrd: resolvedCrd,
        mmid: firstMmid,
        mmType: '',
        symbol: ''
    }), [resolvedCrd, firstMmid]);

    const handleFirmChange = ({setFieldValue}) => {
        setFieldValue('mmid', '');
        setFieldValue('mmType', '');
        setFieldValue('symbol', '');
    };

    const handleFiltersChanged = (newFilterValues) => {
        setFilterValues(newFilterValues);
    };

    const handleSelectionChanged = (newSelectedRows) => {
        setSelectedRows(newSelectedRows);
    };

    const handleAddClick = () => {
        setShowAddModal(true);
    };

    const handleDeleteClick = (event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        if (selectedRows.length === 0) {
            return;
        }
        if (selectedRows.length > MAX_SELECTION) {
            enqueueSnackbar(
                `Maximum ${MAX_SELECTION} appointments can be selected for delete. Please reduce your selection.`,
                {
                    variant: 'error',
                    autoHideDuration: 5000
                }
            );
            return;
        }
        setShowDeleteModal(true);
    };

    const handleModalClose = (isSuccessful, successMessage) => {
        setShowAddModal(false);
        setShowDeleteModal(false);
        setSelectedRows(isSuccessful ? [] : selectedRows);

        if (isSuccessful) {
            // Fallback only — both modals pass an action-specific successMessage (adds:
            // ~1 min / staged near the open; deletes: ~1 min pre-open else next-day). This
            // generic line is intentionally conservative since it can't know the action.
            enqueueSnackbar(
                successMessage || 'MM appointment(s) submitted — additions typically appear within ~1 minute; deletions during trading hours take effect at next-day processing.',
                {
                    variant: 'success',
                    autoHideDuration: 6000
                }
            );
            props.getMmAppointments(filterValues);
        }
    };

    const handleAddModalHide = () => {
        setShowAddModal(false);
    };

    const handleDeleteModalHide = () => {
        setShowDeleteModal(false);
    };

    const hasPermissionCreate = hasPermissions(
        props.user,
        [MmAppointmentPermissions.create],
        [props.platform.mic]
    );
    const hasPermissionDelete = hasPermissions(
        props.user,
        [MmAppointmentPermissions.delete],
        [props.platform.mic]
    );

    const gridProps = useMemo(() => ({
        isLoading: props.mmAppointments.isLoading,
        error: props.mmAppointments.error,
        rowData: props.mmAppointments.rowData,
        more: props.mmAppointments.more,
        totalCount: props.mmAppointments.totalCount
    }), [
        props.mmAppointments.isLoading,
        props.mmAppointments.error,
        props.mmAppointments.rowData,
        props.mmAppointments.more,
        props.mmAppointments.totalCount
    ]);

    if (!isReady) {
        return null;
    }

    return (
        <AgGridProvider
            columnNamespace='mmAppointments'
            columnDefs={props.columnDefs.mmAppointments}
            filtersClassName='row space-bottom-1'
            initialFilters={initialFilters}
            initialPagination={initialPagination}
            onFiltersChanged={handleFiltersChanged}
            onLoad={props.getMmAppointments}
            onColumnsChanged={props.setColumns}
            onSelectionChanged={handleSelectionChanged}
            rowSelection='multiple'
            submitOnLoad={Boolean(firstMmid)}
            {...gridProps}>
            <AgGridProvider.Filters>
                <EntitlementSelect
                    className='col-lg-2 col-md-3 col-sm-6 col-xs-12'
                    name='entitlement'
                />
                {props.firms.length > 1 && (
                    <div className='form-group col-lg-2 col-md-3 col-sm-6 col-xs-12'>
                        <label>Firm</label>
                        <FirmSelect
                            initialValue={props.route.params.crd}
                            name='userCrd'
                            onChange={handleFirmChange}
                            required={true}
                        />
                    </div>
                )}
                <div className='form-group col-lg-2 col-md-3 col-sm-6 col-xs-12'>
                    <label>MMID</label>
                    <MmIdSelect
                        crd={filterValues.userCrd}
                        name='mmid'
                        disableInactiveControl
                        hideEtpid
                        required={true}
                        selectFirstAvailable
                    />
                </div>
                <div className='form-group col-lg-2 col-md-3 col-sm-6 col-xs-12'>
                    <label>MM Type</label>
                    <MMTypesSelect labelField='gboLabel' eligibleOnly />
                </div>
                <div className='form-group col-lg-2 col-md-3 col-sm-6 col-xs-12'>
                    <label>Symbol</label>
                    <NyUppercaseInput
                        name='symbol'
                        maxLength={24}
                    />
                </div>
            </AgGridProvider.Filters>
            <div className='row space-bottom-1'>
                <div className='col-md-12 col-lg-12 col-xl-12 col-sm-12 col-xs-12'>
                    <div className='flex-row flex-align-bottom'>
                        <AgGridProvider.SubmitBtn>
                            Search
                        </AgGridProvider.SubmitBtn>
                        <AgGridProvider.ResetBtn>
                            Reset
                        </AgGridProvider.ResetBtn>

                        <div className='btn-circle-grp flex-pull-right flex-row'>
                            {hasPermissionCreate && (
                                <button
                                    type='button'
                                    className='btn btn-primary space-right-1'
                                    onClick={handleAddClick}>
                                    Add
                                </button>
                            )}

                            {hasPermissionDelete && (
                                <button
                                    type='button'
                                    className='btn btn-danger space-right-1'
                                    onClick={handleDeleteClick}
                                    disabled={selectedRows.length === 0}>
                                    Delete
                                </button>
                            )}

                            <MmAppointmentHoldWindowInfoTooltip />
                            <AgGridAutoRefresh />
                            <AgGridToggleShowFilters />
                            <AgGridExportMenu
                                exportFileName='mmAppointments'
                                exportSheetName={props.platform.name}
                            />
                            <AgGridColumnMenuTrigger />
                        </div>
                    </div>
                </div>
            </div>

            <AgGrid reactNext={true} isRowSelectable={isRowSelectable} />

            <AgGridPagination>
                <option value={25}>25</option>
                <option value={100}>100</option>
                <option value={500}>500</option>
                <option value={1000}>1,000</option>
                <option value={5000}>5,000</option>
                <option value={10000}>10,000</option>
            </AgGridPagination>

            {showAddModal && (
                <AddAppointmentsModal
                    userCrd={filterValues.userCrd}
                    show={showAddModal}
                    hideModal={handleAddModalHide}
                    onClose={handleModalClose}
                />
            )}

            {showDeleteModal && (
                <DeleteConfirmationModal
                    userCrd={filterValues.userCrd}
                    selectedRows={selectedRows}
                    show={showDeleteModal}
                    hideModal={handleDeleteModalHide}
                    onClose={handleModalClose}
                />
            )}
        </AgGridProvider>
    );
};

export const MmAppointmentsPage = connect(
    ['firms', 'columnDefs', 'mmAppointments', 'platform', 'route', 'user'],
    {
        setColumns,
        getMmAppointments,
        createMmAppointments,
        deleteMmAppointments
    }
)(MmAppointmentsPageComponent);
